import { useEffect, useRef, useState } from 'react';
import {
    collection,
    doc,
    onSnapshot,
    orderBy,
    query,
} from 'firebase/firestore';
import { db } from '../../../providers/firebase/firebase';
import {
    attentionRequired,
    isGateStale,
    normalizeGateDoc,
    type GateDoc,
    type GateMode,
} from '../../../types/gates';
import { observedKey, toMs, type SortContext } from '../gateSort';

interface UseGatesResult {
    gates: GateDoc[];
    loading: boolean;
    /** Ordering fallback for docs with no first_requested_at. */
    sortContext: SortContext;
    /** True when the live listen failed (typically a rules permission
     * denial — e.g. a deep link to an account the viewer isn't assigned to,
     * or an operator whose assignment was just revoked). Distinguishes
     * "no requests" from "no access" so the UI doesn't lie about which. */
    error: boolean;
}

/** Write-once stand-in for first_requested_at on legacy docs — never touching an
 *  existing key is what makes it heartbeat-proof. See design/GATE_SORTING.md. */
function trackObserved(
    observed: Map<string, number>,
    docs: GateDoc[],
    batchNow: number
): void {
    for (const g of docs) {
        const key = observedKey(g);
        if (!observed.has(key))
            observed.set(key, toMs(g.updated_at) ?? batchNow);
    }
}

/** Live stream of all gate docs for one account, newest request first. */
export function useGates(accountId: string | null): UseGatesResult {
    const [gates, setGates] = useState<GateDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const observedRef = useRef(new Map<string, number>());

    useEffect(() => {
        if (!accountId) {
            setGates([]);
            setLoading(false);
            setError(false);
            return;
        }
        setLoading(true);
        setError(false);
        const q = query(
            collection(db, `gates/${accountId}/markets`),
            orderBy('updated_at', 'desc')
        );
        const unsub = onSnapshot(
            q,
            snap => {
                const docs = snap.docs.map(d => normalizeGateDoc(d.data()));
                // One clock read per snapshot, not per doc.
                trackObserved(observedRef.current, docs, Date.now());
                setGates(docs);
                setLoading(false);
            },
            () => {
                setGates([]);
                setLoading(false);
                setError(true);
            }
        );
        return () => unsub();
    }, [accountId]);

    return {
        gates,
        loading,
        error,
        sortContext: { observed: observedRef.current },
    };
}

function readMode(
    data: Record<string, unknown> | undefined
): GateMode | undefined {
    const mode = data?.mode;
    return mode === 'auto' || mode === 'gated' ? mode : undefined;
}

/**
 * Live needs-attention count per account for the left-panel cards.
 * status is vestigial (always "pending"), so there is nothing to query
 * server-side — subscribe the market collection and derive from the
 * generation handshake client-side. Stale cards (ended market / stopped
 * heartbeat) don't demand attention.
 *
 * Mode-aware (design/GATE_UI.md §2.4): also subscribes to each account's
 * `gates/{account_id}` control doc so an undecided card doesn't count while
 * the account is in `auto` mode (it auto-approves and is informational);
 * increase requests always count. While an account's mode is still
 * loading/absent, treat it as undefined — `attentionRequired` then falls
 * back to the conservative `gated` behavior.
 */
export function useGateAttentionCounts(
    accountIds: string[]
): Record<string, number> {
    const [gatesByAccount, setGatesByAccount] = useState<
        Record<string, GateDoc[]>
    >({});
    const [modeByAccount, setModeByAccount] = useState<
        Record<string, GateMode | undefined>
    >({});
    const key = [...accountIds].sort().join(',');

    useEffect(() => {
        const ids = key.split(',').filter(Boolean);
        setGatesByAccount({});
        setModeByAccount({});
        const unsubs = ids.flatMap(accountId => {
            const marketsUnsub = onSnapshot(
                query(collection(db, `gates/${accountId}/markets`)),
                snap => {
                    setGatesByAccount(prev => ({
                        ...prev,
                        [accountId]: snap.docs.map(d =>
                            normalizeGateDoc(d.data())
                        ),
                    }));
                },
                () => {
                    setGatesByAccount(prev => ({ ...prev, [accountId]: [] }));
                }
            );
            const modeUnsub = onSnapshot(
                doc(db, `gates/${accountId}`),
                snap => {
                    setModeByAccount(prev => ({
                        ...prev,
                        [accountId]: readMode(snap.data()),
                    }));
                },
                () => {
                    setModeByAccount(prev => ({
                        ...prev,
                        [accountId]: undefined,
                    }));
                }
            );
            return [marketsUnsub, modeUnsub];
        });
        return () => unsubs.forEach(u => u());
    }, [key]);

    const counts: Record<string, number> = {};
    for (const accountId of key.split(',').filter(Boolean)) {
        const gates = gatesByAccount[accountId] ?? [];
        const mode = modeByAccount[accountId];
        counts[accountId] = gates.filter(
            g => attentionRequired(g, mode) && !isGateStale(g)
        ).length;
    }
    return counts;
}
